import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '../../lib/api';
import { RefreshCw, Search } from 'lucide-react';

const InventoryStocks = () => {
    const [stocks, setStocks] = useState([]);
    const [products, setProducts] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [stockRes, prodRes, storeRes] = await Promise.all([
                inventoryAPI.getInventoryStocks(),
                inventoryAPI.getProducts(),
                inventoryAPI.getStores()
            ]);
            setStocks(stockRes.data.data || stockRes.data);
            setProducts(prodRes.data.data || prodRes.data);
            setStores(storeRes.data.data || storeRes.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const getProductName = (id) => products.find(p => p.id === id)?.name || '-';
    const getStoreName = (id) => stores.find(s => s.id === id)?.name || '-';

    const filteredStocks = stocks.filter(s => 
        getProductName(s.product_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getStoreName(s.store_id).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Inventory Stocks</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">View current inventory levels</p>
            </div>
            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Store</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Quantity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reorder Level</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredStocks.length > 0 ? filteredStocks.map(stock => (
                                    <tr key={stock.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{getProductName(stock.product_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{getStoreName(stock.store_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">{stock.quantity || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{stock.reorder_level || '-'}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No stocks found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryStocks;
